<script lang="ts" setup>
	const props = defineProps<{
		metaDetails: RecordSchema
		premium?: string
		isPending: boolean
		isExpired: boolean
	}>()
	const { currencyToNaira } = useCurrency()
	const { formatDate } = useCustomDate()
	const { downloadFile } = useFileExplorer()
	const downloadingDocs = shallowRef(false)
	const { productCategoryType } = useUtils()
	const categoryType = computed(() =>
		productCategoryType(props.metaDetails?.product?.productCategory?.name, props.metaDetails?.product?.code_name)
	)
	const policyDetails = computed(() => {
		const autoPolicy = props.metaDetails as unknown as PolicyResponse
		const expiryDate =
			autoPolicy?.policy_expiration_date && formatDate(autoPolicy.policy_expiration_date, 'DD MMM, YYYY')
		return {
			'Policy number': autoPolicy?.policy_number || autoPolicy?.meta?.sdk_config?.policy_number || '---',
			'Purchase Date': (autoPolicy?.policy_start_date && formatDate(autoPolicy.policy_start_date)) || '',
			'Expiry Date': expiryDate,
			...((!categoryType.value?.isThirdParty && {
				'Inspection Date':
					(autoPolicy.meta?.vehicleInspectionMeta?.timestamp &&
						formatDate(autoPolicy.meta?.vehicleInspectionMeta?.timestamp)) ||
					''
			}) ??
				{}),
			'Vehicle Type': ((autoPolicy?.meta?.vehicle_type as DetailsType)?.name ||
				autoPolicy?.meta?.vehicle_type) as string,
			'Vehicle Manufacturer': ((autoPolicy?.meta?.vehicle_make as DetailsType)?.name ||
				autoPolicy?.meta?.vehicle_make) as string,
			'Vehicle Value': currencyToNaira(autoPolicy?.meta?.vehicle_value || 0),
			'Plate Number': autoPolicy?.meta?.plate_number || autoPolicy?.meta?.vehicle_registration_number || '',
			'Chassis Number': autoPolicy?.meta?.chassis_number || '',
			'Engine Number': autoPolicy?.meta?.engine_number || '',
			'Vehicle Model': ((autoPolicy?.meta?.vehicle_model as DetailsType)?.name ||
				autoPolicy?.meta?.vehicle_model) as string,
			'Vehicle Colour': autoPolicy?.meta?.vehicle_color || '',
			'Vehicle Rate': autoPolicy?.meta?.vehicle_rate || '',
			'Vehicle Premium': currencyToNaira(autoPolicy?.price || props.premium || 0),
			'Policy Certificate': autoPolicy?.policy_certificate_url || ''
		}
	})
	const downloadCert = async (link: string, name = 'certs') => {
		if (!link) {
			return useNotification().alert({
				status: 400,
				message: 'Certificate not available at the moment.'
			})
		}
		try {
			downloadingDocs.value = true
			const fileLink = link.split('.')
			const fileExtension = fileLink[fileLink.length - 1]
			await downloadFile(link, `${name}.${fileExtension}`)
		} finally {
			downloadingDocs.value = false
		}
	}
</script>

<template>
	<div>
		<my-cover-pending-policy-placeholder
			icon-dir="documents"
			title="No Policy Holder Details"
			sub-title="This staff cannot use policy till they activate their plan"
			icon-name="clipboard-document-2"
			:is-pending="isPending"
			use-reminder
		>
			<template #button-group>
				<div></div>
			</template>
		</my-cover-pending-policy-placeholder>
		<my-cover-policy-card v-if="!isPending" :details="policyDetails">
			<template #value="{ record, dataIndex }">
				<template v-if="dataIndex === 'policy_certificate'">
					<div
						class="flex min-h-[44px] w-full max-w-[160px] items-center gap-x-3 rounded-md border border-gray-100 bg-gray-50 p-3"
						@click="downloadCert(record as string)"
					>
						<lazy-tools-icon
							dir="documents"
							:icon="(!record && 'document-text') || 'pdf'"
							:path-class="(!record && 'fill-gray-300') || ''"
						/>
						<div class="flex cursor-pointer items-center justify-between gap-x-2">
							<span v-show="record"> certs.pdf </span>
							<span v-show="!record"> ---- </span>
							<lazy-tools-icon
								v-show="record"
								:dir="!downloadingDocs ? 'system' : 'line'"
								:icon="!downloadingDocs ? 'download' : 'eclipse'"
								:height="20"
								:width="20"
								path-class="stroke-purple-500"
							/>
						</div>
					</div>
				</template>
			</template>
		</my-cover-policy-card>
	</div>
</template>
